import { FC, useContext, useEffect, useRef } from "react";
import { BaseContext, BaseContextProvider } from "@app/network-portfolio-management/contexts/BaseContext";
import { FilterContextProvider } from "@app/network-portfolio-management/contexts/FilterContext";
import { NetworkPortfolioManagementResourceView } from "@app/network-portfolio-management/interfaces";
import { useEnforceViewAccess, useNPMView } from "@app/network-portfolio-management/hooks/useNPMView";
import { ActiveFiltersBar } from "@app/network-portfolio-management/components/ActiveFiltersBar";
import { FilterBar } from "@app/network-portfolio-management/components/FilterBar";
import { RestrictedFiltersNotice } from "@app/network-portfolio-management/components/RestrictedFiltersNotice";
import { StaleSavedViewNotice } from "@app/network-portfolio-management/components/StaleSavedViewNotice";
import { KeyMetrics } from "@app/network-portfolio-management/components/KeyMetrics";
import { ProjectSection } from "@app/network-portfolio-management/components/ProjectSection";
import { QESAlert } from "@app/shared/qes-standards/Alert";

export const DashboardContent: FC = () => {
  const { projectIds, resourcesError } = useContext(BaseContext);
  const isArchived = useNPMView().getCurrentNpmView() === NetworkPortfolioManagementResourceView.Archived;
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) {
      // Archived view: filter bar is not rendered. Reset the stale value so Key Metrics
      // sticks at the correct position (directly below the app header).
      document.querySelector<HTMLElement>(".network-portfolio-management-dashboard")
        ?.style.setProperty("--npm-sticky-header-height", "0px");
      return;
    }

    const observer = new ResizeObserver(() => {
      const dashboard = el.closest<HTMLElement>(".network-portfolio-management-dashboard");
      dashboard?.style.setProperty("--npm-sticky-header-height", `${el.offsetHeight}px`);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isArchived]);

  if (resourcesError) {
    return (
      <QESAlert type="danger" persist={true}>
        {resourcesError}
      </QESAlert>
    );
  }

  return (
    <>
      {!isArchived && (
        <div className="network-portfolio-management-sticky-header" ref={stickyHeaderRef}>
          <RestrictedFiltersNotice />
          <StaleSavedViewNotice />
          <FilterBar />
          <ActiveFiltersBar />
        </div>
      )}
      <KeyMetrics />
      <ProjectSection projectIds={projectIds} />
    </>
  );
};

export const Dashboard: FC = () => {
  useEnforceViewAccess();

  return (
    <FilterContextProvider>
      <BaseContextProvider>
        <div className="network-portfolio-management-dashboard" data-testid="network-portfolio-management-dashboard">
          <DashboardContent />
        </div>
      </BaseContextProvider>
    </FilterContextProvider>
  );
};
